/**
 * Whether the flights up-sell was shown and what the user chose, read back by
 * complete_buy_air to measure the up-sell's effect on flight-only conversion.
 *
 * In sessionStorage rather than on the checkout context: it is recorded on the
 * review page, read at fulfillment, and has to survive the excursion into
 * packages and back.
 *
 * These are decisions, not outcomes. `add_hotel` means the user chose to add a
 * hotel and then booked the flight alone anyway; whether they actually reached
 * the packages funnel is `hotel_up_sell_failed`.
 *
 * Keyed by trip id so a stale interaction can't attach to a later booking in
 * the same tab.
 */

const STORAGE_KEY = "rbc_flight_up_sell_interaction";

/**
 * `skipped` and `not_shown` are distinct: the entry point rendered and was
 * ignored, versus never rendered at all.
 */
export enum UpSellResponse {
  add_hotel = "add_hotel",
  no_thanks = "no_thanks",
  dismissed = "dismissed",
  skipped = "skipped",
  not_shown = "not_shown",
}

export type FlightUpSellInteraction = {
  hotel_up_sell_shown: boolean;
  hotel_up_sell_response: UpSellResponse;
};

const NOT_SHOWN: FlightUpSellInteraction = {
  hotel_up_sell_shown: false,
  hotel_up_sell_response: UpSellResponse.not_shown,
};

/**
 * `hotel_up_sell_shown` isn't stored: a record only exists once the entry point
 * has rendered, so it is true whenever the trip matches.
 */
type Stored = { tripId: string; response: UpSellResponse };

const read = (): Stored | undefined => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw == null) return undefined;
    const parsed = JSON.parse(raw) as Partial<Stored>;
    return typeof parsed?.tripId === "string" ? (parsed as Stored) : undefined;
  } catch {
    return undefined;
  }
};

const write = (value: Stored): void => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // sessionStorage can be unavailable (private mode); this is best-effort.
  }
};

/** Until the user decides anything, a rendered entry point counts as skipped. */
export const recordUpSellShown = (tripId: string | undefined): void => {
  if (!tripId) return;
  // Re-viewing isn't a reset: leave any decision already made for this trip.
  if (read()?.tripId === tripId) return;

  write({ tripId, response: UpSellResponse.skipped });
};

/** The last decision in the session wins. */
export const recordUpSellResponse = (
  tripId: string | undefined,
  response: UpSellResponse
): void => {
  if (!tripId) return;
  write({ tripId, response });
};

/** Defaults to not-shown, including when the stored trip doesn't match. */
export const getFlightUpSellInteraction = (
  tripId: string | undefined
): FlightUpSellInteraction => {
  if (typeof window === "undefined" || !tripId) return NOT_SHOWN;

  const stored = read();
  if (stored?.tripId !== tripId) return NOT_SHOWN;

  return {
    hotel_up_sell_shown: true,
    hotel_up_sell_response: stored.response,
  };
};
